import React from 'react'
import {_} from 'plugins/i18n'
import {cn, roundRate} from 'app/lib'
import Text from 'app/Components/twComponents/Text'
import {MoneyValue} from 'app/Components/Numbers'
import {COND_PAYINADVANCE, COND_PAYTHERE} from 'app/_server/database/constants'
import {normalizeExtras} from '../helpers'

export const TextRowDefault: React.FC<{
	value: number | string | JSX.Element
}> = ({value}) => {
	return (
		<div className="flex items-center justify-start">
			<Text variant="base">{value}</Text>
		</div>
	)
}

export const MoneyRowDefault: React.FC<{
	value: number | string | JSX.Element
}> = ({value}) => {
	return (
		<div className="flex items-center justify-end">
			<Text variant="base">
				<MoneyValue value={value as number} />
			</Text>
		</div>
	)
}

export const Row: React.FC<{
	Left?: React.FC<{value: number | string | JSX.Element | undefined}>
	Right?: React.FC<{value: number | string | JSX.Element | undefined}>
	valueLeft?: number | string | JSX.Element
	valueRight?: number | string | JSX.Element | null
	rowWrapClassName?: string
}> = ({
	Left = TextRowDefault,
	Right = MoneyRowDefault,
	valueLeft,
	valueRight,
	rowWrapClassName = 'text-onTertiary',
}) => {
	return (
		<li
			className={cn(
				'flex w-full items-center justify-between',
				rowWrapClassName
			)}
		>
			{Left && valueLeft && <Left value={valueLeft} />}
			{Right && valueRight && <Right value={valueRight} />}
		</li>
	)
}

export const PaymentSchedulePanel = ({
	contract,
}: {
	contract: Pick<Contract, 'dueDates'>
}) => {
	const {dueDates} = contract || {}

	const deposit = dueDates?.find(d => d.isDeposit)
	const total = dueDates?.find(d => !d.isDeposit)
	const guarantee = dueDates?.find(d => d.isGuarantee)
	// total + guarantee - deposit
	const sellTotalToPayBalance =
		((guarantee || total)?.amount || 0) - (deposit?.amount || 0)

	return (
		<ul className="bg-capGoldLight flex flex-col gap-4 rounded-lg p-4">
			<Row valueLeft={_('PaymentSchedule')} rowWrapClassName="font-bold" />

			{deposit?.date ? (
				<div className="flex flex-col gap-1">
					<Row
						valueLeft={
							<span>
								{deposit?.date} {_`Deposit`}
							</span>
						}
						valueRight={deposit?.amount}
					/>
					<Text className="text-tertiary text-left">{_('DepositInfo')}</Text>
				</div>
			) : null}
			{total?.date ? (
				<div className="flex flex-col gap-1">
					<Row
						valueLeft={
							<span>
								{total?.date} {_`Saldo`}
							</span>
						}
						valueRight={sellTotalToPayBalance}
					/>
					<Text className="text-tertiary text-left">{_('TotalPriceInfo')}</Text>
				</div>
			) : null}
		</ul>
	)
}

export const BaseCostsPanel = ({
	contract,
}: {
	contract: Pick<Contract, 'prices'>
}) => {
	const {prices} = contract || {}
	const {
		adminFee,
		insuranceToPay,
		optionsTotal,
		rentalDiscountRate,
		sellRental,
		sellRentalBase,
		sellRentalBaseWithAdjust,
		sellRentalDiscount,
		sellTotal,
		stayDays,
		touristTax,
	} = prices || {}

	return (
		<ul className="flex flex-col gap-2">
			<Row
				valueLeft={
					<span>
						<span className="font-bold">
							<MoneyValue value={sellRental / stayDays} />
						</span>{' '}
						{_`perNight`}{' '}
						{rentalDiscountRate > 0 && (
							<>
								<MoneyValue
									value={sellRentalBase / stayDays}
									className="line-through"
								/>{' '}
								<span className="font-bold">
									{-roundRate(rentalDiscountRate * 100)}% {_`discount`}
								</span>
							</>
						)}
					</span>
				}
			/>
			<Row
				valueLeft={
					<span>
						<MoneyValue value={sellRentalBaseWithAdjust / stayDays} /> x{' '}
						{stayDays} {_`nights`}
					</span>
				}
				valueRight={sellRentalBaseWithAdjust}
			/>
			{rentalDiscountRate ? (
				<Row valueLeft={_`Promotion`} valueRight={-sellRentalDiscount} />
			) : null}
			{insuranceToPay ? (
				<Row valueLeft={_`CancellationInsurance`} valueRight={insuranceToPay} />
			) : null}
			{adminFee ? <Row valueLeft={_`AdmFee`} valueRight={adminFee} /> : null}
			{touristTax ? (
				<Row valueLeft={_`TouristTaxEstimate`} valueRight={touristTax} />
			) : null}
			{optionsTotal ? (
				<Row valueLeft={_`Extras`} valueRight={optionsTotal} />
			) : null}
			<div className="border border-solid border-gray-300" />
			<Row
				rowWrapClassName="font-bold"
				valueLeft={_`Total`}
				valueRight={sellTotal}
			/>
		</ul>
	)
}

export const OverviewPanel = ({
	contract,
}: {
	contract: Pick<Contract, 'prices' | 'options' | 'dueDates'>
}) => {
	const {options, prices} = contract || {}

	const {
		adminFee,
		guaranteePayNow,
		insuranceToPay,
		rentalDiscountRate,
		sellRentalBaseWithAdjust,
		sellRentalDiscount,
		sellTotal,
		stayDays,
		touristTax,
	} = prices || {}

	const normalizedOptions = normalizeExtras(options)
	const extrasPayInAdvance = normalizedOptions.filter(
		({condition, count}) => condition === COND_PAYINADVANCE && count
	)
	const extrasPayThere = normalizedOptions.filter(
		({condition, count}) => condition === COND_PAYTHERE && count
	)

	return (
		<>
			<ul className="flex flex-col gap-2">
				<span className="font-bold">
					<Row valueLeft={_('PayInAdvance')} />
				</span>
				<Row
					valueLeft={
						<span>
							<MoneyValue value={sellRentalBaseWithAdjust / stayDays} /> x{' '}
							{stayDays} {_`nights`}
						</span>
					}
					valueRight={sellRentalBaseWithAdjust}
				/>
				{rentalDiscountRate ? (
					<Row valueLeft={_`Promotion`} valueRight={-sellRentalDiscount} />
				) : null}
				{insuranceToPay ? (
					<Row
						valueLeft={_`CancellationInsurance`}
						valueRight={insuranceToPay}
					/>
				) : null}
				{adminFee ? <Row valueLeft={_`AdmFee`} valueRight={adminFee} /> : null}
				{touristTax ? (
					<Row valueLeft={_`TouristTaxEstimate`} valueRight={touristTax} />
				) : null}
				{/* Extras paid in advance */}
				{extrasPayInAdvance.map(({optionId, price, count}, i) =>
					price ? (
						<Row key={i} valueLeft={_(optionId)} valueRight={price * count!} />
					) : null
				)}

				<div className="border border-solid border-gray-300" />

				<Row
					rowWrapClassName="font-bold"
					valueLeft={_`Total`}
					valueRight={sellTotal}
				/>
				{guaranteePayNow ? (
					<Row valueLeft={_`Guarantee`} valueRight={guaranteePayNow} />
				) : null}
			</ul>

			<PaymentSchedulePanel contract={contract} />

			{/* Extras paid on site */}
			{extrasPayThere?.length ? (
				<ul className="bg-capGoldLight flex flex-col gap-2 rounded-lg p-4">
					<span className="font-bold">
						<Row valueLeft={_('PaymentOnSite')} />
					</span>
					{extrasPayThere.map(({optionId, price, count}, i) => (
						<Row
							key={i}
							valueLeft={_(optionId)}
							valueRight={price ? price * count! : null}
						/>
					))}
				</ul>
			) : null}
		</>
	)
}
